import { queryOptions } from "@tanstack/react-query";
import type { ApiListRequest } from "../../../../../@types/services/Api";
import { useListPlantsQueryOptions } from "../../../../../__generated__/orval/endpoints/plants/plants";
import { listQuotations } from "../../../../../__generated__/orval/endpoints/quotations/quotations";
import type { EntitiesPlantListResponse } from "../../../../../__generated__/orval/models/entitiesPlantListResponse.zod";
import {
  type ListQuotationsParams,
  ListQuotationsParams as ListQuotationsParamsSchema,
} from "../../../../../__generated__/orval/models/listQuotationsParams.zod";
import { fetchMaterialGroups } from "../../../../../services/materialGroups";
import { fetchPurchaseGroups } from "../../../../../services/purchaseGroup";
import { fetchSegments } from "../../../../../services/segments";
import { listStatuses } from "../../../../../services/statuses";
import { fetchUsers } from "../../../../../services/users";

type QuotationsOrderKey = Extract<
  keyof ListQuotationsParams,
  `order[${string}]`
>;

type ArrayCoercibleFilterKey =
  | "assigned_to"
  | "material_group"
  | "plant"
  | "purchase_group"
  | "segment"
  | "status";

export type QuotationsFilter = Omit<
  ListQuotationsParams,
  QuotationsOrderKey | ArrayCoercibleFilterKey | "items_per_page" | "page"
> & {
  assigned_to?: number | number[];
  material_group?: number | number[];
  plant?: number | number[];
  purchase_group?: number | number[];
  segment?: number | number[];
  status?: string | string[];
};

export type QuotationsOrder = Partial<
  Record<QuotationsOrderKey, "asc" | "desc">
>;

const normalizeArrayFilter = <T>(
  value: T | T[] | undefined,
): T[] | undefined => {
  if (value == null) {
    return undefined;
  }

  return Array.isArray(value) ? value : [value];
};

export const buildListQuotationsParams = ({
  filters,
  orders,
  ...rest
}: ApiListRequest<
  QuotationsFilter,
  QuotationsOrder
> = {}): ListQuotationsParams => {
  const normalizedFilters = {
    ...filters,
    assigned_to: normalizeArrayFilter(filters?.assigned_to),
    material_group: normalizeArrayFilter(filters?.material_group),
    plant: normalizeArrayFilter(filters?.plant),
    purchase_group: normalizeArrayFilter(filters?.purchase_group),
    segment: normalizeArrayFilter(filters?.segment),
    status: normalizeArrayFilter(filters?.status),
  };

  return ListQuotationsParamsSchema.parse({
    ...normalizedFilters,
    ...orders,
    ...rest,
  });
};

export const quotationsListQueryOptions = (
  params: ApiListRequest<QuotationsFilter, QuotationsOrder>,
) =>
  queryOptions({
    queryFn: () => listQuotations(buildListQuotationsParams(params)),
    queryKey: ["quotations", "list", params],
  });

// Filters

export const plantListQueryOptions = (
  ids?: EntitiesPlantListResponse["results"][number]["id"][],
) =>
  useListPlantsQueryOptions(
    { id: ids },
    {
      query: {
        enabled: !!ids && ids.length > 0,
        select: (data) => data.results,
        staleTime: 1000 * 60 * 5, // 5 minutes
      },
    },
  );

export const purchaseGroupListQueryOptions = (ids?: string[]) =>
  queryOptions({
    enabled: !!ids && ids.length > 0,
    queryFn: () => fetchPurchaseGroups({ filters: { id: ids } }),
    queryKey: ["purchaseGroups", "list", ids],
    select: (data) => data.results,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

export const userListQueryOptions = (params?: {
  ids?: number[];
  searchTerm?: string;
}) =>
  queryOptions({
    enabled:
      !!params &&
      ((params.ids && params.ids.length > 0) || !!params.searchTerm),
    queryFn: () =>
      fetchUsers({
        filters: {
          equinor_user: true,
          id: params?.ids,
          name_or_login: params?.searchTerm,
        },
      }),
    queryKey: ["users", "list", params],
    select: (data) => data.results,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

export const materialGroupListQueryOptions = (materialGroupIds?: number[]) =>
  queryOptions({
    enabled: !!materialGroupIds && materialGroupIds.length > 0,
    queryFn: () => fetchMaterialGroups({ filters: { id: materialGroupIds } }),
    queryKey: ["materialGroups", "list", materialGroupIds],
    select: (data) => data.results,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

export const segmentListQueryOptions = (segmentIds?: number[]) =>
  queryOptions({
    enabled: !!segmentIds && segmentIds.length > 0,
    queryFn: () => fetchSegments({ filters: { id: segmentIds } }),
    queryKey: ["segments", "list", segmentIds],
    select: (data) => data.results,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

export const statusListQueryOptions = (statusNames?: string[]) =>
  queryOptions({
    enabled: !!statusNames && statusNames.length > 0,
    queryFn: () =>
      listStatuses({
        filters: {
          name: statusNames,
          scope: "quotations",
        },
      }),
    queryKey: ["statuses", "list", statusNames],
    select: (data) => data.results,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
